import {
  Dispatch,
  SetStateAction,
  useCallback,
  useEffect,
  useState,
} from 'react';

import { BehaviorSubject } from 'rxjs';
import { distinctUntilChanged } from 'rxjs/operators';

export function useBehaviorSubject<T>(
  subject: BehaviorSubject<T>,
): [T, Dispatch<SetStateAction<T>>] {
  // note(andyl): use the function form of useState to avoid
  // calling subject.getValue() in the initial state in case the value
  // in the behavior subject is a function.
  const [state, setState] = useState(() => subject.getValue());
  useEffect(() => {
    const sub = subject.pipe(distinctUntilChanged()).subscribe({
      next: setState,
    });
    return () => sub.unsubscribe();
  }, [setState, subject]);
  const update = useBehaviorSubjectUpdater(subject);
  return [state, update];
}

export function useBehaviorSubjectUpdater<T>(
  subject: BehaviorSubject<T>,
): Dispatch<SetStateAction<T>> {
  return useCallback(
    valOrUpdate => {
      if (typeof valOrUpdate === 'function') {
        const current = subject.getValue();
        const next = (valOrUpdate as (p: T) => T)(current);
        if (current !== next) {
          subject.next(next);
        }
      } else {
        subject.next(valOrUpdate);
      }
    },
    [subject],
  );
}
